/**
 * Supabase implementation of DatabaseAdapter.
 * Used for the cloud / Pro tier.
 */

import { createClient } from '@/lib/supabase/client';
import type {
  DatabaseAdapter,
  QueryFilter,
  QueryOptions,
  UpsertOptions,
} from './interfaces';

function applyFilters(query: any, filters: QueryFilter[]): any {
  for (const f of filters) {
    switch (f.operator) {
      case 'eq':    query = query.eq(f.column, f.value); break;
      case 'neq':   query = query.neq(f.column, f.value); break;
      case 'gt':    query = query.gt(f.column, f.value); break;
      case 'gte':   query = query.gte(f.column, f.value); break;
      case 'lt':    query = query.lt(f.column, f.value); break;
      case 'lte':   query = query.lte(f.column, f.value); break;
      case 'like':  query = query.like(f.column, f.value as string); break;
      case 'ilike': query = query.ilike(f.column, f.value as string); break;
      case 'in':    query = query.in(f.column, f.value as unknown[]); break;
    }
  }
  return query;
}

export class SupabaseDatabaseAdapter implements DatabaseAdapter {
  private get client() {
    return createClient();
  }

  async select<T = Record<string, unknown>>(
    table: string,
    options?: QueryOptions
  ): Promise<T[]> {
    let query = this.client.from(table).select('*');
    if (options?.filters?.length) query = applyFilters(query, options.filters);
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, {
        ascending: options.orderBy.ascending ?? true,
      });
    }
    if (options?.limit) query = query.limit(options.limit);
    if (options?.offset) query = (query as any).range(options.offset, options.offset + (options.limit ?? 1000) - 1);
    const { data, error } = await query;
    if (error) { console.error(`[SupabaseDB] select(${table}):`, error.message); return []; }
    return (data || []) as T[];
  }

  async selectOne<T = Record<string, unknown>>(
    table: string,
    options?: QueryOptions
  ): Promise<T | null> {
    let query = this.client.from(table).select('*');
    if (options?.filters?.length) query = applyFilters(query, options.filters);
    const { data, error } = await (query as any).maybeSingle();
    if (error) { console.error(`[SupabaseDB] selectOne(${table}):`, error.message); return null; }
    return (data as T) ?? null;
  }

  async insert<T = Record<string, unknown>>(
    table: string,
    rows: Partial<T> | Partial<T>[]
  ): Promise<T[]> {
    const { data, error } = await this.client
      .from(table)
      .insert(rows as any)
      .select();
    if (error) { console.error(`[SupabaseDB] insert(${table}):`, error.message); return []; }
    return (data || []) as T[];
  }

  async update<T = Record<string, unknown>>(
    table: string,
    updates: Partial<T>,
    filters: QueryFilter[]
  ): Promise<T[]> {
    let query = this.client.from(table).update(updates as any);
    query = applyFilters(query, filters);
    const { data, error } = await (query as any).select();
    if (error) { console.error(`[SupabaseDB] update(${table}):`, error.message); return []; }
    return (data || []) as T[];
  }

  async upsert<T = Record<string, unknown>>(
    table: string,
    rows: Partial<T> | Partial<T>[],
    options?: UpsertOptions
  ): Promise<T[]> {
    const { data, error } = await this.client
      .from(table)
      .upsert(rows as any, options?.onConflict ? { onConflict: options.onConflict } : undefined)
      .select();
    if (error) { console.error(`[SupabaseDB] upsert(${table}):`, error.message); return []; }
    return (data || []) as T[];
  }

  async delete(table: string, filters: QueryFilter[]): Promise<boolean> {
    let query = this.client.from(table).delete();
    query = applyFilters(query, filters);
    const { error } = await query;
    if (error) { console.error(`[SupabaseDB] delete(${table}):`, error.message); return false; }
    return true;
  }

  async rpc<T = unknown>(fn: string, params?: Record<string, unknown>): Promise<T> {
    const { data, error } = await this.client.rpc(fn, params as any);
    if (error) throw new Error(`[SupabaseDB] rpc(${fn}): ${error.message}`);
    return data as T;
  }
}
